/**
 * `?step=clinic` on a pre-filled cart deep link (`?products=<package>,…`):
 * the caller already chose the package AND its add-ons, so the flow opens on
 * the clinic step instead of walking the visitor back through product and
 * add-ons. The marketing-site quiz sends it only when its recommendation
 * carries add-ons; a package-only link keeps the add-ons step, which is where
 * those visitors add most of their extras.
 *
 * `?step=addons` opens the add-ons only on the membership-first flow's
 * one-off handoff (ONE_TIME_HANDOFF): the visitor picked the test on its
 * tests screen, so the product step's cards would only ask again (Eugene,
 * 22.10). Every other link that says `step=addons` (the insurance checker's)
 * keeps the product step, as before.
 *
 * Read from the URL as it was at page load: `useStepHistory` can rewrite
 * `?step=` (to `product`, when it validates the step before the catalog has
 * loaded) before `initializeFromUrl` gets to apply the cart.
 */
const SEARCH_AT_LOAD =
  typeof window === "undefined" ? "" : window.location.search;

/** The membership-first flow's mark on its one-off links
 *  (apps/membership-first/src/oneTimeBooking.ts). */
export const ONE_TIME_HANDOFF = { param: "from", value: "membership-first" };

/**
 * The page loaded on the membership-first flow's one-off handoff link itself:
 * the mark, its `step=addons` and its `?products=` cart. Such a visit is a
 * one-off booking, so the store drops whatever membership a restored session
 * holds, and its events are tagged in PostHog (Eugene, 22.10).
 *
 * The mark alone would not do: nothing strips `from=`, so it stays on every
 * URL of the visit, the Stripe return URL included. A reload or a TWINT
 * return later in that visit must keep a membership the visitor then added
 * on purpose (the MRI bundle on the add-ons, the details-page upsell).
 */
export const isOneTimeHandoff = (search: string = SEARCH_AT_LOAD): boolean => {
  const params = new URLSearchParams(search);
  return (
    params.get(ONE_TIME_HANDOFF.param) === ONE_TIME_HANDOFF.value &&
    params.get("step") === "addons" &&
    !!params.get("products")
  );
};

export type CartEntryStep = "clinic" | "addons";

export const cartEntryStep = (
  hasPackage: boolean,
  search: string = SEARCH_AT_LOAD,
): CartEntryStep | null => {
  if (!hasPackage) return null;
  const params = new URLSearchParams(search);
  const step = params.get("step");
  if (step === "clinic") return "clinic";
  if (
    step === "addons" &&
    params.get(ONE_TIME_HANDOFF.param) === ONE_TIME_HANDOFF.value
  ) {
    return "addons";
  }
  return null;
};
